// src/components/audioUrl.js
//
// Free pack audio is PERSISTED as a direct Cloud Storage URL
// (https://storage.googleapis.com/<bucket>/<path>, built in
// functions/src/admin/getUploadUrl.js). That form is authorized by Cloud IAM, NOT by
// Firebase storage.rules -- so serving it would need an allUsers public-read IAM
// binding, which the project's org policy forbids and which would also expose paid/**.
//
// Instead the reader PLAYS free audio via the Firebase download endpoint
// (https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded-path>?alt=media),
// which IS governed by storage.rules. public/samples/** is `allow read: if true`
// (storage.rules), so the tokenless alt=media URL is served to anyone -- no IAM
// public-read and no download token. We DERIVE the object path from the already-stored
// storage.googleapis.com URL, so existing free items work WITHOUT re-upload.
//
// Pure (no React / Firebase SDK imports) so it is unit-testable under `node --test`.

const GCS_PREFIX = 'https://storage.googleapis.com/';
const FB_HOST = 'https://firebasestorage.googleapis.com';

// Map a stored free-audio URL to a rules-governed Firebase download URL.
//   https://storage.googleapis.com/<bucket>/<path>
//     -> https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<enc(path)>?alt=media
// Already a firebasestorage.googleapis.com URL -> returned unchanged (forward-compatible
// if the upload path is ever switched to persist that form). Anything blank/unexpected
// -> returned unchanged (defensive: never throw inside a render path).
export function freePlaybackUrl(storageUrl) {
    const url = typeof storageUrl === 'string' ? storageUrl.trim() : '';
    if (!url) return url;
    if (url.indexOf(FB_HOST) === 0) return url;        // already rules-governed
    if (url.indexOf(GCS_PREFIX) !== 0) return url;     // not the direct-GCS form we know
    const rest = url.slice(GCS_PREFIX.length);         // "<bucket>/<path>[?query]"
    const noQuery = rest.split('?')[0];                // drop any query (none expected)
    const slash = noQuery.indexOf('/');
    if (slash === -1) return url;                      // no path segment -> leave as-is
    const bucket = noQuery.slice(0, slash);
    const objectPath = noQuery.slice(slash + 1);
    if (!bucket || !objectPath) return url;
    return FB_HOST + '/v0/b/' + bucket + '/o/' + encodeURIComponent(objectPath) + '?alt=media';
}
